import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import type {
  IntegrationReadiness,
  ReadinessIssue,
} from "features/integrationsBeta/integrationsBeta";
import {
  sectionForIssue,
  worstSeverity,
  type SettingsSection,
} from "features/integrationsBeta/readinessUi";
import { useState } from "react";

interface Props {
  readiness?: IntegrationReadiness;
  onNavigate: (section: SettingsSection, issue: ReadinessIssue) => void;
}

const COLLAPSED_COUNT = 5;

/**
 * "Setup incomplete" banner above the Settings tabs: one row per readiness
 * issue with an "Open" link that jumps to the section (and connection) where
 * the problem is fixed. Hidden while everything is configured.
 */
export default function ReadinessBanner({ readiness, onNavigate }: Props) {
  const [showAll, setShowAll] = useState(false);
  if (!readiness || readiness.issues.length === 0) return null;

  const severity = worstSeverity(readiness.issues) ?? "warning";
  const visible = showAll
    ? readiness.issues
    : readiness.issues.slice(0, COLLAPSED_COUNT);
  const hidden = readiness.issues.length - visible.length;

  return (
    <Alert severity={severity}>
      <AlertTitle>
        {severity === "error" ? "Setup incomplete" : "Setup notes"}
      </AlertTitle>
      <Stack spacing={0.75}>
        {visible.map((issue, index) => (
          <Box
            key={`${issue.code}-${issue.step_id ?? ""}-${
              issue.connection_id ?? ""
            }-${index}`}
            sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
          >
            <Chip
              size="small"
              variant="outlined"
              color={issue.severity === "error" ? "error" : "warning"}
              label={issue.severity === "error" ? "Error" : "Warning"}
            />
            <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
              {issue.message}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ flexShrink: 0 }}
              onClick={() => onNavigate(sectionForIssue(issue), issue)}
            >
              Open
            </Link>
          </Box>
        ))}
      </Stack>
      {hidden > 0 && (
        <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowAll(true)}>
          Show all ({readiness.issues.length})
        </Button>
      )}
    </Alert>
  );
}
